import React, { forwardRef, useState } from 'react';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  helperText,
  className = '',
  rows = 4,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  
  // Separate out event handlers from other props
  const { onFocus, onBlur, onChange, ...otherProps } = props;
  
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    setInternalValue(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    setInternalValue(e.target.value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInternalValue(value);
  };

  // Check if label should be floating
  const currentValue = internalValue || props.value || '';
  const hasValue = String(currentValue).trim() !== '';
  const isLabelFloating = isFocused || hasValue;

  return (
    <div className="w-full">
      <div className="relative">
        <textarea
          ref={ref}
          rows={rows}
          className={`
            peer w-full px-4 pt-6 pb-2 min-h-[120px] border border-gray-300 rounded-lg resize-none
            focus:outline-none focus:ring-0 focus:border-blue-500
            transition-all duration-200
            placeholder:text-transparent
            ${error ? 'border-red-300 focus:border-red-500' : 'hover:border-gray-400'}
            ${className}
          `}
          placeholder=" "
          onFocus={(e) => {
            handleFocus(e);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            handleBlur(e);
            onBlur?.(e);
          }}
          onChange={(e) => {
            handleChange(e);
            onChange?.(e);
          }}
          {...otherProps}
        />
        
        {label && (
          <label
            className={`
              absolute left-3 transition-all duration-200 pointer-events-none
              ${isLabelFloating
                ? '-top-2.5 text-xs text-gray-500 bg-white px-2'
                : 'top-4 text-base text-gray-400'
              }
              ${error && isLabelFloating ? 'text-red-500' : ''}
              ${isFocused && !error ? 'text-blue-500' : ''}
            `}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
      </div>
      
      {(error || helperText) && (
        <div className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;